import type { MediaFit, MediaItem, MediaKind, Playlist } from '@/types'

import { cycleDurationMs } from './schedule'

export const MIN_DURATION_MS = 1_000
export const MAX_DURATION_MS = 6 * 60 * 60 * 1_000
export const MAX_TRANSITION_MS = 5_000

export interface NormalizeDefaults {
  imageDurationMs: number
  transitionMs: number
  fit: MediaFit
}

export interface NormalizeResult {
  playlist: Playlist
  /** Human readable reasons why entries were dropped or coerced. */
  problems: string[]
}

const KINDS: MediaKind[] = ['image', 'video']
const FITS: MediaFit[] = ['cover', 'contain']

/**
 * This playlist does not exist on the server, so the caller should fall back to the
 * default one.
 */
export class ManifestMissingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ManifestMissingError'
  }
}

/** The server answered, but not with a manifest. Worth retrying. */
export class ManifestHttpError extends Error {
  constructor(
    readonly status: number,
    url: string,
  ) {
    super(`HTTP ${status} for ${url}`)
    this.name = 'ManifestHttpError'
  }
}

/**
 * Decides whether a manifest response means "not there" or "something is broken".
 *
 * A missing manifest does not reliably arrive as a 404: a static host with a
 * single-page-app fallback answers an unknown path with `index.html` and status 200.
 * Treating an unparseable body as missing means a screen with a mistyped slug shows
 * the default playlist instead of retrying forever against a black screen.
 */
export function parseManifestResponse(status: number, body: string, url: string): unknown {
  if (status === 404) throw new ManifestMissingError(`HTTP 404 for ${url}`)
  if (status < 200 || status > 299) throw new ManifestHttpError(status, url)

  try {
    return JSON.parse(body) as unknown
  } catch {
    throw new ManifestMissingError(`${url} did not return JSON`)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Turns whatever the manifest happens to contain into a playlist that is safe to
 * schedule. A malformed or partially broken manifest must never take a screen
 * down, so unusable entries are dropped and the rest keeps playing.
 */
export function normalizePlaylist(
  raw: unknown,
  name: string,
  defaults: NormalizeDefaults,
): NormalizeResult {
  const problems: string[] = []

  if (!isRecord(raw)) {
    return { playlist: { name, revision: 'invalid', items: [], cycleMs: 0 }, problems: ['Manifest is not an object'] }
  }

  const rawItems = Array.isArray(raw.items) ? raw.items : []
  if (!Array.isArray(raw.items)) problems.push('Manifest has no "items" array')

  const items: MediaItem[] = []
  const seenIds = new Set<string>()

  rawItems.forEach((entry, position) => {
    if (!isRecord(entry)) {
      problems.push(`Item ${position} is not an object`)
      return
    }

    const src = asString(entry.src)
    if (!src) {
      problems.push(`Item ${position} has no "src"`)
      return
    }

    const kindValue = asString(entry.kind)
    const kind = KINDS.find((candidate) => candidate === kindValue)
    if (!kind) {
      problems.push(`Item ${position} (${src}) has unsupported kind "${kindValue ?? ''}"`)
      return
    }

    let id = asString(entry.id) ?? src
    if (seenIds.has(id)) {
      problems.push(`Duplicate id "${id}" — suffixed to keep items distinct`)
      id = `${id}#${position}`
    }
    seenIds.add(id)

    const mediaDurationMs = asFiniteNumber(entry.mediaDurationMs)
    const requestedDuration =
      asFiniteNumber(entry.durationMs) ??
      (kind === 'video' ? mediaDurationMs : null) ??
      defaults.imageDurationMs
    const durationMs = clamp(Math.round(requestedDuration), MIN_DURATION_MS, MAX_DURATION_MS)
    if (durationMs !== Math.round(requestedDuration)) {
      problems.push(`Item ${id} duration ${requestedDuration}ms clamped to ${durationMs}ms`)
    }

    const fitValue = asString(entry.fit)
    const fit = FITS.find((candidate) => candidate === fitValue) ?? defaults.fit

    const transitionValue = asFiniteNumber(entry.transitionMs) ?? defaults.transitionMs
    const transitionMs = clamp(Math.round(transitionValue), 0, MAX_TRANSITION_MS)

    // A video shorter than its slot has to repeat, otherwise it freezes on the last frame.
    const loop =
      typeof entry.loop === 'boolean'
        ? entry.loop
        : kind === 'video' && mediaDurationMs !== null && durationMs > mediaDurationMs + 250

    items.push({
      id,
      kind,
      src,
      poster: asString(entry.poster),
      durationMs,
      mediaDurationMs: kind === 'video' ? mediaDurationMs : null,
      width: asFiniteNumber(entry.width),
      height: asFiniteNumber(entry.height),
      fit,
      loop,
      transitionMs,
    })
  })

  const revision = asString(raw.revision) ?? items.map((item) => item.id).join('|')

  return {
    playlist: {
      name: asString(raw.name) ?? name,
      revision,
      items,
      cycleMs: cycleDurationMs(items),
    },
    problems,
  }
}

/** Joins a manifest-relative path onto the configured media base. */
export function resolveMediaUrl(base: string, path: string): string {
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('/')) return path
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}
